/** @jsxImportSource @emotion/react */
import { getResource } from "../language/resources/getResource";
import { Column, Row } from "../layout/flexbox";
import infoImg from "../assets/icons/info.svg";
import { TooltipFC } from "../layout/TooltipFC";
import { DROP_SHADOW, PAGE_TITLE } from "../styles/styles";
import { useSelector } from "react-redux";
import { IAppState } from "../core/redux/IAppState";
import { css } from "@emotion/react";
import { SidebarMenu } from "./SidebarMenu";
import { productAdminMenuItems } from "./productAdminMenu";
import { Outlet } from "react-router-dom";
import { WHITE } from "../styles/colors";

const infoStyle = css`
  width: 20px;
  height: 20px;
  margin-left: 10px;
  margin-bottom: 5px;
  pointer-events: initial;
`;

const titleRow = css`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  margin-top: 30px;
  margin-bottom: 15px;
`;

const pageContent = css`
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: visible;
  padding: 8px;             /* Leave space for the outer drop shadows */
  box-sizing: border-box;
  background: transparent;
`;

const contentRow = css`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;             /* IMPORTANT for inner scrolling */
  min-width: 0;
  align-items: stretch;      /* Stretch children to equal heights when possible */
  gap: 10px;                 /* Space between sidebar and main card */
`;

const sidebarColumn = css`
  flex: 0 0 auto;
  display: flex;  /* Ensure sidebar can stretch */
`;

const mainCard = css`
  /* Occupies the remaining width beside the sidebar */
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  width: 100%; /* Take full width of available space */
  height: 100%;

  background: ${WHITE};
  ${DROP_SHADOW};
  overflow-x: visible;      
  overflow-y: visible;

  position: relative;
  z-index: 1;

  display: flex;
  flex-direction: column;
`;

const mainScrollArea = css`
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  width: 100%;
  overflow-x: visible; /* Allow content to determine width and handle its own horizontal overflow */
  overflow-y: visible; /* Allow content to determine height and handle its own vertical overflow */
  display: flex;
  flex-direction: column;

  /* Inner padding for page content: */
  padding: 5px;
`;

const outletArea = css`
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;


export const ProductAdminLayout = () => {
  const userRole = useSelector((state: IAppState) => state.userState?.role ?? "agent");
  const resources = useSelector((state: IAppState) => state.locale);

  const onTooltipClick = (event) => event.preventDefault();

  return (
    <Column rootCss={pageContent}>
      <div css={titleRow}>
        <span css={PAGE_TITLE}>
          {getResource(resources, "PRODUCT_ADMIN_PAGE_TITLE")}
        </span>
        <TooltipFC
          content={<span>{getResource(resources, "PRODUCT_ADMIN_PAGE_INFO")}</span>}
          children={
            <img
              alt="ⓘ"
              src={infoImg}
              css={infoStyle}
              onClick={onTooltipClick}
            />
          }
        />
      </div>

      <Row rootCss ={contentRow}>
        <div css={sidebarColumn}>
          <SidebarMenu
            items={productAdminMenuItems}
            userRole={userRole}
            resources={resources}
          />
        </div>
        {/* This is the only place that should scroll */}

      <section css={mainCard}>
        <main css={mainScrollArea}>
          <div css={outletArea}>
            <Outlet />
          </div>
        </main>
      </section>

      </Row>
    </Column>
  );
};
